import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import styled, { css } from 'styled-components';
import { FileOutlined, LinkOutlined, PictureOutlined } from '@ant-design/icons';
import { sanitizeHtml } from '@utils/sanitizeHtml';
import {
    normalizePhaseLinkUrl,
    resolveAttachmentUrl,
    isLikelyImageAttachmentUrl,
} from '@hooks/projects/projectHelpers';
import { InlinePrimaryLink } from '@/components/ui/Link';

const INLINE_ICON_ROW = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    minWidth: 0,
};

function MarkdownInlineIconLabel({ icon, children }) {
    return (
        <span style={INLINE_ICON_ROW}>
            <span style={{ display: 'inline-flex', lineHeight: 0, flexShrink: 0, color: 'currentColor' }} aria-hidden>
                {icon}
            </span>
            <span style={{ minWidth: 0 }}>{children}</span>
        </span>
    );
}

function MarkdownRichInlineAnchor({ href, children, attachmentItems, onLinkClick }) {
    const h = href || '';
    const items = Array.isArray(attachmentItems) ? attachmentItems : [];

    if (h.startsWith('#mention-')) {
        // Mentions are shown as non-clickable primary links.
        return (
            <span
                style={{
                    fontWeight: 600,
                    color: 'var(--ant-color-primary, #7c3aed)',
                }}
            >
                {children}
            </span>
        );
    }

    const attMatch = /^#attachment-(\d+)$/.exec(h);
    if (attMatch) {
        const idx = parseInt(attMatch[1], 10);
        const filesOnly = items.filter((x) => String(x.type || 'file').toLowerCase() !== 'link');
        const item = filesOnly[idx] ?? items[idx];
        const resolved = item?.url ? resolveAttachmentUrl(item.url) : '';
        if (!resolved) {
            return <InlinePrimaryLink inheritMarkdownSize>{children}</InlinePrimaryLink>;
        }
        const fileIcon = isLikelyImageAttachmentUrl(resolved) ? (
            <PictureOutlined style={{ fontSize: '0.95em' }} />
        ) : (
            <FileOutlined style={{ fontSize: '0.95em' }} />
        );
        return (
            <InlinePrimaryLink
                href={resolved}
                target="_blank"
                rel="noopener noreferrer"
                inheritMarkdownSize
                onClick={(e) => {
                    onLinkClick?.({
                        event: e,
                        href: resolved,
                        rawHref: h,
                        isImage: isLikelyImageAttachmentUrl(resolved),
                    });
                }}
            >
                <MarkdownInlineIconLabel icon={fileIcon}>{children}</MarkdownInlineIconLabel>
            </InlinePrimaryLink>
        );
    }

    const normalized = normalizePhaseLinkUrl(h);
    if (!normalized) {
        return (
            <InlinePrimaryLink inheritMarkdownSize>
                {children}
            </InlinePrimaryLink>
        );
    }
    return (
        <InlinePrimaryLink
            href={normalized}
            target="_blank"
            rel="noopener noreferrer"
            inheritMarkdownSize
            onClick={(e) => {
                onLinkClick?.({
                    event: e,
                    href: normalized,
                    rawHref: h,
                    isImage: isLikelyImageAttachmentUrl(normalized),
                });
            }}
        >
            <MarkdownInlineIconLabel icon={<LinkOutlined style={{ fontSize: '0.95em' }} />}>{children}</MarkdownInlineIconLabel>
        </InlinePrimaryLink>
    );
}

function MarkdownDefaultAnchor({ href, children, onLinkClick }) {
    const h = href || '';
    const normalized = normalizePhaseLinkUrl(h) || h;
    const external = /^https?:\/\//i.test(normalized) || /^mailto:/i.test(normalized);
    return (
        <InlinePrimaryLink
            href={normalized}
            target={external ? '_blank' : undefined}
            rel={external ? 'noopener noreferrer' : undefined}
            inheritMarkdownSize
            onClick={(e) => {
                onLinkClick?.({
                    event: e,
                    href: normalized,
                    rawHref: h,
                    isImage: isLikelyImageAttachmentUrl(normalized),
                });
            }}
        >
            {children}
        </InlinePrimaryLink>
    );
}

const MarkdownRoot = styled.div`
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
    font-size: ${({ $compact }) => ($compact ? '13px' : '14px')};
    line-height: 1.5;
    word-break: break-word;
    overflow-wrap: break-word;
    color: inherit;
    user-select: text;
    -webkit-user-select: text;

    ${({ $lineClamp }) =>
        $lineClamp &&
        css`
            overflow: hidden;
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: ${$lineClamp};
            text-overflow: ellipsis;
        `}

    ${({ $unwrapPreCode }) =>
        $unwrapPreCode &&
        css`
            pre:not([data-markdown-fenced]),
            pre[data-markdown-fenced] {
                margin: 0 0 8px 0 !important;
                overflow-x: visible !important;
                overflow-y: visible !important;
                white-space: pre-wrap !important;
                word-wrap: break-word !important;
                word-break: break-word !important;
                padding: 0 !important;
                border: none !important;
                background: transparent !important;
                border-radius: 0 !important;
                box-shadow: none !important;
            }
            pre:not([data-markdown-fenced]) code,
            pre[data-markdown-fenced] code {
                padding: 0 !important;
                display: inline !important;
                font-family: var(--font-family-geist), system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
                font-size: inherit !important;
                line-height: inherit !important;
                background: transparent !important;
                color: inherit !important;
                white-space: pre-wrap !important;
                word-wrap: break-word !important;
                word-break: break-word !important;
                border: none !important;
                border-radius: 0 !important;
            }
            :not(pre) > code,
            p code,
            li code,
            td code,
            th code,
            blockquote > code {
                font-family: var(--font-family-geist), system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
                font-size: inherit !important;
                padding: 0 !important;
                border-radius: 0 !important;
                background: transparent !important;
                color: inherit !important;
                white-space: pre-wrap !important;
                word-wrap: break-word !important;
                word-break: break-word !important;
                border: none !important;
            }
        `}

    p {
        margin: 0 0 ${({ $compact }) => ($compact ? '4px' : '6px')} 0;
    }
    p:last-child {
        margin-bottom: 0;
    }

    ul,
    ol {
        margin: 0 0 ${({ $compact }) => ($compact ? '4px' : '6px')} 0;
        padding-left: 1.75em;
        padding-inline-end: 12px;
        box-sizing: border-box;
        list-style-position: outside;
    }
    li {
        margin: 0 0 4px 0;
        padding-left: 0.35em;
        display: list-item;
        list-style-position: outside;
    }
    ul > li::marker,
    ol > li::marker {
        color: ${({ theme }) =>
        theme?.colors?.background?.default === '#141414' ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.55)'};
    }
    ol {
        list-style-type: decimal;
    }

    /* Inline code only; fenced pre is styled by ChatMessage CodeBlock (data-markdown-fenced). */
    :not(pre) > code,
    p code,
    li code,
    td code,
    th code,
    blockquote > code {
        font-size: 0.9em;
        padding: 2px 6px;
        border-radius: 4px;
        background: ${({ theme }) =>
        theme?.colors?.background?.default === '#141414' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
        user-select: text !important;
        -webkit-user-select: text !important;
    }
    pre:not([data-markdown-fenced]) {
        margin: 0 0 8px 0;
        overflow-x: auto;
        padding: 10px 12px;
        border-radius: 4px;
        border: 1px solid
            ${({ theme }) =>
        theme?.colors?.background?.default === '#141414' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'};
        background: ${({ theme }) =>
        theme?.colors?.background?.default === '#141414' ? '#1a1a1d' : '#ebebeb'};
        user-select: text !important;
        -webkit-user-select: text !important;
    }
    pre:not([data-markdown-fenced]) code {
        padding: 0 !important;
        display: block;
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
        font-size: 12.5px;
        line-height: 1.45;
        background: transparent !important;
        color: ${({ theme }) =>
        theme?.colors?.background?.default === '#141414' ? 'rgba(235,235,235,0.88)' : '#1d1c1d'};
        user-select: text !important;
        -webkit-user-select: text !important;
    }

    /* Chat fenced blocks: inner code must never pick up inline-code chip styles; links must not inherit anchor color: inherit from MarkdownRoot. */
    pre[data-markdown-fenced] {
        user-select: text !important;
        -webkit-user-select: text !important;
    }
    pre[data-markdown-fenced] code {
        font-size: inherit !important;
        padding: 0 !important;
        margin: 0 !important;
        border: none !important;
        border-radius: 0 !important;
        background: transparent !important;
        box-shadow: none !important;
        color: inherit !important;
        user-select: text !important;
        -webkit-user-select: text !important;
    }
    pre[data-markdown-fenced] a {
        color: ${({ theme }) =>
        theme?.colors?.background?.default === '#141414' ? '#6cb6ff' : '#0969da'} !important;
        text-decoration: underline !important;
        cursor: pointer !important;
        pointer-events: auto !important;
        user-select: text !important;
        -webkit-user-select: text !important;
    }

    blockquote a,
    p code a,
    li code a,
    td code a,
    th code a {
        color: ${({ theme }) =>
        theme?.colors?.background?.default === '#141414' ? '#6cb6ff' : '#0969da'} !important;
        text-decoration: underline !important;
        cursor: pointer !important;
        pointer-events: auto !important;
    }

    a {
        ${({ $richInlineAnchors, $linkPrimary, theme }) =>
        !$richInlineAnchors &&
        css`
                color: ${$linkPrimary
                ? (theme?.colors?.primary ?? 'var(--ant-color-primary, #7c3aed)')
                : 'inherit'};
            `}
        text-decoration: ${({ $richInlineAnchors }) => ($richInlineAnchors ? 'none' : 'underline')};
    }
    a:hover {
        opacity: ${({ $richInlineAnchors }) => ($richInlineAnchors ? 1 : 0.85)};
    }

    blockquote {
        margin: 0 0 8px 0;
        padding: 10px 12px;
        border-radius: 8px;
        box-sizing: border-box;
        max-width: 100%;
        min-width: 0;
        overflow-wrap: break-word;
        word-break: break-word;
        font-style: normal;
        font-family: inherit;
        font-size: 13px;
        line-height: 1.5;
        border: 1px solid
            ${({ theme }) =>
        theme?.colors?.background?.default === '#141414' ? 'rgba(255,255,255,0.14)' : '#d1d5db'};
        border-left: 3px solid
            ${({ theme }) => (theme?.colors?.background?.default === '#141414' ? '#71717a' : '#9ca3af')};
        background: ${({ theme }) =>
        theme?.colors?.background?.default === '#141414' ? '#3f3f46' : '#e5e7eb'};
        color: ${({ theme }) =>
        theme?.colors?.background?.default === '#141414' ? 'rgba(255,255,255,0.94)' : '#1f2937'};
        user-select: text !important;
        -webkit-user-select: text !important;
    }
    blockquote p {
        margin: 0 0 6px 0;
        color: inherit;
    }
    blockquote p:last-child {
        margin-bottom: 0;
    }
    blockquote code {
        font-style: normal;
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
        font-size: 0.92em;
        color: inherit;
        background: ${({ theme }) =>
        theme?.colors?.background?.default === '#141414' ? 'rgba(0,0,0,0.22)' : 'rgba(0,0,0,0.08)'} !important;
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
        margin: 0 0 6px 0;
        font-weight: 600;
        line-height: 1.3;
    }
    h1 {
        font-size: 1.35em;
    }
    h2 {
        font-size: 1.2em;
    }
    h3 {
        font-size: 1.1em;
    }

    hr {
        margin: 8px 0;
        border: none;
        border-top: 1px solid
            ${({ theme }) =>
        theme?.colors?.background?.default === '#141414' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)'};
    }

    table {
        border-collapse: collapse;
        width: 100%;
        margin: 0 0 8px 0;
    }
    th,
    td {
        padding: 6px 10px;
        border: 1px solid
            ${({ theme }) =>
        theme?.colors?.background?.default === '#141414' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)'};
        text-align: left;
    }
    th {
        font-weight: 600;
    }
`;

/**
 * Single markdown display component. Renders markdown (and raw HTML) via react-markdown + remark-gfm + rehype-raw only (no custom parser).
 * Use everywhere we show stored markdown/HTML: chat messages, project tabs (blockers, discussion, feedback), sprint/task descriptions.
 *
 * @param {string} [source] - Content to render (markdown or HTML). Also accepts `html` as alias.
 * @param {string} [emptyFallback] - When source is empty, show this (e.g. '—'). Omit to render nothing.
 * @param {React.ElementType} [component] - Wrapper element. Also accepts `as` as alias. Default 'div'.
 * @param {string} [className] - Optional class for wrapper.
 * @param {object} [components] - Custom react-markdown components (e.g. override `a`, `code`).
 * @param {string} [variant] - 'default' | 'compact'. Compact uses slightly tighter spacing.
 * @param {boolean} [linkPrimary] - When true, inline links use theme primary color (e.g. phase descriptions).
 * @param {boolean} [richInlineAnchors] - When true, mentions, attachment links, and URLs render as compact pill chips (phase/milestone descriptions).
 * @param {Array<{ type?: string, url: string, title?: string }>} [attachmentItems] - Resolves `#attachment-n` in HTML to file URLs (same order as editor file attachments).
 * @param {React.Ref} [contentRef] - Ref to the inner markdown body root (for measuring line-clamp overflow).
 */
export function Markdown({
    source,
    html,
    emptyFallback,
    component,
    as,
    className,
    components,
    variant = 'default',
    lineClamp,
    linkPrimary,
    richInlineAnchors,
    unwrapPreCode,
    attachmentItems,
    onLinkClick,
    contentRef,
    ...rest
}) {
    const raw = source ?? html ?? '';
    const trimmed = typeof raw === 'string' ? raw.trim() : '';
    const Wrapper = component ?? as ?? 'div';

    const mergedComponents = useMemo(() => {
        // Always use our inline link styling unless caller overrides `a`.
        const base = {
            a: ({ href, children, node: _n }) => (
                <MarkdownDefaultAnchor href={href} onLinkClick={onLinkClick}>
                    {children}
                </MarkdownDefaultAnchor>
            ),
        };
        if (!richInlineAnchors) {
            return { ...base, ...components };
        }
        return {
            ...base,
            ...components,
            a: ({ href, children, node: _n }) => (
                <MarkdownRichInlineAnchor href={href} attachmentItems={attachmentItems} onLinkClick={onLinkClick}>
                    {children}
                </MarkdownRichInlineAnchor>
            ),
        };
    }, [components, richInlineAnchors, attachmentItems, onLinkClick]);

    if (!trimmed) {
        if (emptyFallback !== undefined && emptyFallback !== null) {
            return (
                <Wrapper className={className} {...rest}>
                    {emptyFallback}
                </Wrapper>
            );
        }
        return null;
    }

    const hasInlineTags = /<[^>]+>/.test(trimmed);
    let safeSource = hasInlineTags ? sanitizeHtml(trimmed) : trimmed;
    /** sanitizeHtml round-trips through innerHTML; leading `>` in text becomes `&gt;`, breaking markdown blockquotes. */
    if (hasInlineTags) {
        safeSource = safeSource.replace(/^&gt;\s/gm, '> ');
    }

    return (
        <Wrapper className={className} {...rest}>
            <MarkdownRoot
                ref={contentRef}
                $compact={variant === 'compact'}
                $lineClamp={lineClamp}
                $linkPrimary={linkPrimary}
                $richInlineAnchors={richInlineAnchors}
                $unwrapPreCode={unwrapPreCode ?? richInlineAnchors}
            >
                <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={mergedComponents}>
                    {safeSource}
                </ReactMarkdown>
            </MarkdownRoot>
        </Wrapper>
    );
}

export default Markdown;
